import type {
    ChecklistData,
    ChecklistElements,
    ChecklistFilterGroup,
    ChecklistObject,
    ChecklistToc,
    ChecklistTocElement,
    CollectionDataResponse,
    CommentsResponse,
} from "../types";
import {getStatusDisplayName} from "./statusNames";
import {
    ChecklistItemType,
    Status,
    StatusToDataStatus,
    WITH_COMMENTS,
} from "../types";

// --- helpers ---

export const hasAnyComments = (
    nodeId: string,
    commentsData?: CommentsResponse,
): boolean => {
    if (!commentsData) return false;
    const element = commentsData.elementdata[nodeId];
    if (!element) return false;
    return element.ci_comments?.length > 0;
};

const extractTextFromElement = (
    elementId: string,
    elements: ChecklistElements,
    visited: Set<string> = new Set(),
): string => {
    if (visited.has(elementId)) return "";
    visited.add(elementId);

    const element = elements[elementId];
    if (!element) return "";

    const texts: string[] = [];
    for (const child of element.children || []) {
        if ("text" in child) {
            texts.push(child.text);
        } else if ("id" in child) {
            texts.push(extractTextFromElement(child.id, elements, visited));
        }
    }
    return texts.join(" ");
};

// --- view filter ---

function checkNodeViewFilter(
    node: ChecklistTocElement,
    labelData: Record<string, string>,
    objects: Record<string, ChecklistObject>,
): boolean {
    if (!node.view || node.view.length === 0) return true;

    if (node.viewByGroup) {
        return Object.entries(node.viewByGroup).every(
            ([groupId, allowedLabelIds]) => {
                const selectedLabel = labelData[groupId];
                return selectedLabel && allowedLabelIds.includes(selectedLabel);
            },
        );
    }

    // Fallback: compute from view array
    const groupIds = new Set<string>();
    node.view.forEach((labelId) => {
        const labelObject = objects[labelId];
        if (labelObject && labelObject.groupId) {
            groupIds.add(labelObject.groupId);
        }
    });

    return Array.from(groupIds).every((groupId) => {
        return labelData[groupId] && node.view!.includes(labelData[groupId]);
    });
}

/**
 * Strips the CMS's editorial markers from a rendered checkpoint title.
 * Control's backend-owned checklist preparation mirrors these rules.
 */
export function pruneChecklistTitle(title: string | undefined): string {
    if (!title) return "";
    return title.replace("[Infopunkt]", "").replace("[Uten tittel]", "").trim();
}

export function filterTocByViewFilters(
    toc: ChecklistToc,
    labelData: Record<string, string>,
    objects: Record<string, ChecklistObject>,
): ChecklistToc {
    const filtered: ChecklistToc = {};

    const processNode = (nodeId: string): boolean => {
        const node = toc[nodeId];
        if (!node) return false;

        if (!checkNodeViewFilter(node, labelData, objects)) return false;

        if (!node.children || node.children.length === 0) {
            filtered[nodeId] = {...node};
            return true;
        }

        const remainingChildren: string[] = [];
        for (const childId of node.children) {
            if (processNode(childId)) {
                remainingChildren.push(childId);
            }
        }

        if (remainingChildren.length > 0) {
            filtered[nodeId] = {
                ...node,
                children: remainingChildren,
            };

            const childInfoStatus = new Set<boolean>();
            if (filtered[nodeId].children) {
                for (const child of filtered[nodeId].children!) {
                    if (filtered[child]) {
                        childInfoStatus.add(filtered[child].info ?? false);
                    }
                }
            }
            if (childInfoStatus.size === 1 && childInfoStatus.has(true)) {
                filtered[nodeId].info = true;
            }
            return true;
        }

        return false;
    };

    const allChildIds = new Set<string>();
    Object.values(toc).forEach((node) => {
        node.children?.forEach((childId) => allChildIds.add(childId));
    });

    const rootIds = Object.keys(toc).filter((id) => !allChildIds.has(id));
    for (const rootId of rootIds) {
        processNode(rootId);
    }

    return filtered;
}

export interface ViewFilteringResult {
    /** Active label per view group id: collection > default collection > first label. */
    labelData: Record<string, string>;
    viewFilteredToc: ChecklistToc;
    /** Empty string when the root itself is filtered away. */
    effectiveRootNodeId: string;
}

/**
 * The checklist view's label selection + TOC filtering as one pure step, so
 * non-view consumers (control run input) apply exactly the same rules as
 * the rendered checklist. `useViewFiltering` is the hook form of this.
 *
 * `overrides` (label id per group id) wins over the derived selection —
 * ignored per group when the id is not one of that group's labels.
 */
export function computeViewFiltering(
    checklistData: ChecklistData,
    collectionData: CollectionDataResponse | undefined,
    defaultCollectionData: CollectionDataResponse | undefined,
    overrides?: Record<string, string>,
): ViewFilteringResult {
    const labelData: Record<string, string> = {};
    const viewGroups = checklistData.viewGroups ?? [];

    if (viewGroups.length) {
        const collectionLabelIds = new Set(
            (collectionData?.labels ?? []).map((l) => l.labelId),
        );
        const defaultCollectionLabelIds = new Set(
            (defaultCollectionData?.labels ?? []).map((l) => l.labelId),
        );

        viewGroups.forEach((group) => {
            const overrideId = overrides?.[group.id];
            const label =
                (overrideId
                    ? group.labels.find((l) => l.id === overrideId)
                    : undefined) ??
                group.labels.find((l) => collectionLabelIds.has(l.id)) ??
                group.labels.find((l) =>
                    defaultCollectionLabelIds.has(l.id),
                ) ??
                group.labels[0];
            if (label) labelData[group.id] = label.id;
        });
    }

    const viewFilteredToc = filterTocByViewFilters(
        checklistData.toc,
        labelData,
        checklistData.objects ?? {},
    );

    const effectiveRootNodeId = viewFilteredToc[
        checklistData.rootNodeId ?? ""
    ]
        ? (checklistData.rootNodeId ?? "")
        : "";

    return {labelData, viewFilteredToc, effectiveRootNodeId};
}

// --- search ---

function checkSearchMatch(
    node: ChecklistTocElement,
    elements: ChecklistElements,
    searchTerm: string,
): boolean {
    if (!searchTerm || searchTerm.length === 0) return true;

    const lowerSearchTerm = searchTerm.toLowerCase();

    if (node.title?.toLowerCase().includes(lowerSearchTerm)) return true;

    const elementIds = [node.ingress, node.description, node.law].filter(
        (id): id is string => Boolean(id),
    );

    for (const elementId of elementIds) {
        const text = extractTextFromElement(elementId, elements);
        if (text.toLowerCase().includes(lowerSearchTerm)) return true;
    }

    return false;
}

// --- show filters ---

function checkShowFilters(
    node: ChecklistTocElement,
    statusFilterSelected: Record<string, (Status | string)[]>,
    nodeStatus: Status | -1,
    hasComments: boolean,
): boolean {
    const filterGroupIds = Object.keys(statusFilterSelected);
    if (filterGroupIds.length === 0) return true;

    for (const groupId of filterGroupIds) {
        const selectedFilters = statusFilterSelected[groupId];
        if (!selectedFilters || selectedFilters.length === 0) continue;

        let groupPasses = false;

        if (groupId === "status") {
            const matchesStatus = selectedFilters.includes(
                nodeStatus as Status,
            );
            const matchesComments =
                selectedFilters.includes(WITH_COMMENTS) && hasComments;
            groupPasses = matchesStatus || matchesComments;
        } else {
            const nodeLabelsForThisGroup = node.showByGroup?.[groupId] || [];
            groupPasses = selectedFilters.some((filter) =>
                nodeLabelsForThisGroup.includes(filter as string),
            );
        }

        if (!groupPasses) return false;
    }

    return true;
}

// --- visibility computation ---

export function computeHiddenIds(
    viewFilteredToc: ChecklistToc,
    rootNodeId: string,
    statusFilterSelected: Record<string, (Status | string)[]>,
    searchTerm: string,
    elements: ChecklistElements,
    collectionData: CollectionDataResponse | undefined,
    commentsData: CommentsResponse | undefined,
    infoIds: string[],
): {
    hiddenIds: Set<string>;
    searchHitIds: Set<string>;
    statusMap: Record<string, Status | -1>;
} {
    const hiddenIds = new Set<string>();
    const searchHitIds = new Set<string>();
    const statusMap: Record<string, Status | -1> = {};
    const infoIdSet = new Set(infoIds);

    const processNode = (
        nodeId: string,
    ): {visible: boolean; status: Status | -1} => {
        const node = viewFilteredToc[nodeId];
        if (!node) return {visible: false, status: -1};

        const isLeaf = !node.children || node.children.length === 0;
        const isInfoNode = infoIdSet.has(nodeId);

        let hasVisibleChild = false;
        const childStatuses: (Status | -1)[] = [];

        if (!isLeaf) {
            for (const childId of node.children!) {
                const childResult = processNode(childId);
                if (childResult.visible) hasVisibleChild = true;
                if (!infoIdSet.has(childId)) {
                    childStatuses.push(childResult.status);
                }
            }
        }

        let nodeStatus: Status | -1;
        if (isInfoNode) {
            nodeStatus = -1;
        } else if (childStatuses.length > 0) {
            const uniqueStatuses = new Set(childStatuses);
            if (uniqueStatuses.size === 1) {
                nodeStatus = childStatuses[0];
            } else {
                nodeStatus = Status.todo;
            }
        } else {
            nodeStatus =
                collectionData?.checkitems?.[nodeId]?.status ?? Status.todo;
        }

        statusMap[nodeId] = nodeStatus;

        const matchesSearch = checkSearchMatch(node, elements, searchTerm);
        if (matchesSearch && searchTerm.length > 0) {
            searchHitIds.add(nodeId);
        }

        const nodeHasComments = commentsData
            ? hasAnyComments(nodeId, commentsData)
            : false;

        const passesShowFilter = checkShowFilters(
            node,
            statusFilterSelected,
            nodeStatus,
            nodeHasComments,
        );

        const selfVisible = passesShowFilter && matchesSearch;
        const visible = isLeaf ? selfVisible : hasVisibleChild || selfVisible;

        if (!visible) hiddenIds.add(nodeId);

        return {visible, status: nodeStatus};
    };

    if (rootNodeId && viewFilteredToc[rootNodeId]) {
        processNode(rootNodeId);
    }

    return {hiddenIds, searchHitIds, statusMap};
}

// --- tree/toc utilities ---

export const allExpanded = (
    viewFilteredToc: ChecklistToc,
    nodeId: string,
    openItems: Record<string, boolean>,
    hiddenIds: Set<string>,
): boolean => {
    if (!openItems[nodeId]) return false;
    const node = viewFilteredToc[nodeId];
    if (node?.children) {
        return node.children
            .filter((childId) => !hiddenIds.has(childId))
            .every((childId) =>
                allExpanded(viewFilteredToc, childId, openItems, hiddenIds),
            );
    }
    return true;
};

export const getVisibleItemIds = (
    viewFilteredToc: ChecklistToc,
    rootNodeId: string,
    hiddenIds: Set<string>,
): Record<string, boolean> => {
    const result: Record<string, boolean> = {};
    if (!rootNodeId) return result;

    const traverse = (nodeId: string) => {
        if (hiddenIds.has(nodeId)) return;
        const node = viewFilteredToc[nodeId];
        if (!node) return;

        if (node.checkItemType === ChecklistItemType.CheckItemParent) {
            result[nodeId] = true;
        }
        node.children?.forEach(traverse);
    };

    traverse(rootNodeId);
    return result;
};

export const getAncestorIds = (
    id: string,
    tocParentMap: Record<string, string>,
): string[] => {
    const ancestorIds = [id];
    let currentId = tocParentMap[id];
    while (currentId) {
        ancestorIds.unshift(currentId);
        currentId = tocParentMap[currentId];
    }
    return ancestorIds;
};

export const getLeafNodeIdsFromToc = (
    toc: ChecklistToc,
    nodeId: string,
): string[] => {
    const leafNodeIds: string[] = [];
    const node = toc[nodeId];
    if (!node) return leafNodeIds;

    if (node.children && node.children.length > 0) {
        node.children.forEach((childId) => {
            const child = toc[childId];
            if (child?.checkItemType === ChecklistItemType.CheckItem) {
                leafNodeIds.push(childId);
            } else {
                leafNodeIds.push(...getLeafNodeIdsFromToc(toc, childId));
            }
        });
    }
    return leafNodeIds;
};

export const getRelatedDocuments = (
    toc: ChecklistToc,
    rootNodeId: string,
): Record<string, {id: string; name: string; resourceTypeId?: string}> => {
    const ret: Record<
        string,
        {id: string; name: string; resourceTypeId?: string}
    > = {};
    if (!rootNodeId || !toc[rootNodeId]) return ret;

    const traverse = (nodeId: string) => {
        const node = toc[nodeId];
        if (!node) return;

        if (node.related && node.related.length > 0) {
            node.related.forEach((related) => {
                ret[related.topicId] = {
                    id: related.topicId,
                    name: related.name,
                    resourceTypeId: String(related.resourceTypeId),
                };
            });
        }
        if (node.children && node.children.length > 0) {
            node.children.forEach((childId) => traverse(childId));
        }
    };

    traverse(rootNodeId);
    return ret;
};

export const getRelatedDocumentsOrder = (
    toc: ChecklistToc,
    rootNodeId: string,
): Record<string, number> => {
    const ret: Record<string, number> = {};
    if (!rootNodeId || !toc[rootNodeId]) return ret;
    let counter = 0;

    const traverse = (nodeId: string) => {
        const node = toc[nodeId];
        if (!node) return;

        if (node.related && node.related.length > 0) {
            node.related.forEach((related) => {
                ret[related.topicId] = counter;
                counter++;
            });
        }
        if (node.children && node.children.length > 0) {
            node.children.forEach((childId) => traverse(childId));
        }
    };

    traverse(rootNodeId);
    return ret;
};

export const calculateCollectionProgress = (
    viewFilteredToc: ChecklistToc,
    rootNodeId: string,
    collectionData: CollectionDataResponse,
    infoIds: string[],
): {max: number; value: number} => {
    const infoIdSet = new Set(infoIds);
    let max = 0;
    let value = 0;

    const processNode = (nodeId: string) => {
        const node = viewFilteredToc[nodeId];
        if (!node) return;

        if (infoIdSet.has(nodeId)) return;

        if (!node.children || node.children.length === 0) {
            max += 1;
            const status = collectionData?.checkitems?.[nodeId]?.status;
            if (status !== undefined && status !== Status.todo) {
                value += 1;
            }
        } else {
            node.children.forEach(processNode);
        }
    };

    if (rootNodeId && viewFilteredToc[rootNodeId]) {
        processNode(rootNodeId);
    }

    return {max: Math.max(max, 1), value};
};

// --- filter group descriptors ---

const getStatusFilterGroup = (
    groupId: string,
    groupLabels: (string | Status)[],
    objects: Record<string, ChecklistObject>,
    isStatus?: boolean,
): ChecklistFilterGroup => {
    const ret: ChecklistFilterGroup = {name: "", labels: []};

    if (isStatus) {
        ret.name = "status";
        ret.labels = groupLabels.map((label) => ({
            name: getStatusDisplayName(label as Status),
            id: StatusToDataStatus[label as Status],
        }));
    } else {
        const groupObject = objects[groupId];
        if (groupObject) {
            ret.name = groupObject.name;
            ret.labels = groupLabels
                .map((label) => {
                    const labelObject = objects[label as string];
                    if (labelObject) {
                        return {name: labelObject.name, id: labelObject.id};
                    }
                    return null;
                })
                .filter((label) => label !== null);
        }
    }
    return ret;
};

export const getChecklistFilterGroups = (
    statusFilterSelected: Record<string, (Status | string)[]>,
    objects: Record<string, ChecklistObject>,
): ChecklistFilterGroup[] => {
    const statusFilterGroup = statusFilterSelected.status
        ? getStatusFilterGroup(
              "status",
              statusFilterSelected.status,
              objects,
              true,
          )
        : undefined;
    const otherFilterGroups = Object.keys(statusFilterSelected)
        .filter((key) => key !== "status")
        .map((key) =>
            getStatusFilterGroup(key, statusFilterSelected[key], objects, false),
        );
    const ret: ChecklistFilterGroup[] = [];
    if (statusFilterGroup) ret.push(statusFilterGroup);
    return [...ret, ...otherFilterGroups];
};
